import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "rsuite";
import { toast } from "react-toastify";
import { MdContentCopy, MdErrorOutline } from "react-icons/md";
import api from "../../../../services/axios";
import {
  ClientPortalUser,
  GrantClientAccessRequest,
  PORTAL_AREA_OPTIONS,
  PortalProfile,
} from "../../../../interfaces/ClientPortalUser";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";
import { FalhaEnvio, enviarEmSequencia, separarEmails } from "../../../../utils/separarEmails";

type Props = { clientId?: number; open: boolean };

type ResetState = {
  userId: string;
  email: string;
  loading: boolean;
  temporaryPassword?: string;
};

const labelCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";
const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";


/** Áreas concedidas: as do perfil escolhido; sem perfil conhecido, as colunas do vínculo. */
const grantedAreas = (u: ClientPortalUser, profile?: PortalProfile) => {
  const source = profile ?? u;
  return PORTAL_AREA_OPTIONS.filter(({ key }) => source[key]).map(({ label }) => label);
};

const PortalUsersSection: React.FC<Props> = ({ clientId, open }) => {
  const [users, setUsers] = useState<ClientPortalUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [sectionError, setSectionError] = useState("");
  const [savingIds, setSavingIds] = useState<string[]>([]);
  const [reset, setReset] = useState<ResetState | null>(null);

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newProfileId, setNewProfileId] = useState("");
  const [portalProfiles, setPortalProfiles] = useState<PortalProfile[]>([]);
  const [profilesError, setProfilesError] = useState(false);
  const [granting, setGranting] = useState(false);
  const [grantFailures, setGrantFailures] = useState<FalhaEnvio[]>([]);
  const { validos: emailsValidos, invalidos: emailsInvalidos } = useMemo(() => separarEmails(newEmail), [newEmail]);

  const load = useCallback((id: number) => {
    setLoading(true);
    setLoadError("");
    api
      .get(`/Client/Clients/${id}/users`)
      .then((r) => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => {
        console.error("Erro ao carregar usuários do portal", e);
        setLoadError("Não foi possível carregar os usuários. Feche e abra o cliente para tentar de novo.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open || !clientId) return;
    setProfilesError(false);
    api
      .get("/Client/portal-profiles")
      .then((r) => {
        const list: PortalProfile[] = Array.isArray(r.data) ? r.data : [];
        setPortalProfiles(list);
      })
      .catch((e) => {
        console.error("Erro ao carregar perfis de portal", e);
        setProfilesError(true);
      });
    setNewEmail("");
    setNewPassword("");
    setSectionError("");
    setReset(null);
    load(clientId);
  }, [open, clientId, load]);

  const replaceUser = (updated: ClientPortalUser) =>
    setUsers((list) => list.map((u) => (u.userId === updated.userId ? updated : u)));

  const handleChangeProfile = async (u: ClientPortalUser, profileId: number) => {
    if (!clientId) return;
    setSectionError("");
    setSavingIds((ids) => [...ids, u.userId]);
    try {
      const r = await api.put(`/Client/Clients/${clientId}/users/${u.userId}/perfil`, { profileId });
      replaceUser(r.data ?? { ...u, profileId });
    } catch (e) {
      console.error("Erro ao salvar perfil do usuário", e);
      setSectionError(
        `Não foi possível salvar o perfil de ${u.email ?? "este usuário"}. Nada foi alterado; tente de novo.`
      );
    } finally {
      setSavingIds((ids) => ids.filter((id) => id !== u.userId));
    }
  };

  const handleGrant = async () => {
    if (!clientId || emailsValidos.length === 0 || emailsInvalidos.length > 0) return;
    setGranting(true);
    setGrantFailures([]);
    const { enviados, falhas } = await enviarEmSequencia(
      emailsValidos,
      (email) => {
        const body: GrantClientAccessRequest = {
          email,
          password: newPassword || null,
          ...(newProfileId ? { profileId: Number(newProfileId) } : {}),
        };
        return api.post(`/Client/Clients/${clientId}/users`, body);
      },
      (e) => {
        console.error("Erro ao conceder acesso", e);
        return mensagemDoServidor(e, "Erro ao conceder acesso.");
      }
    );
    if (falhas.length === 0) {
      toast.success(enviados.length === 1 ? "Acesso ao portal concedido." : `${enviados.length} acessos ao portal concedidos.`);
      setNewEmail("");
      setNewPassword("");
      setNewProfileId("");
    } else if (emailsValidos.length === 1) {
      toast.error(falhas[0].mensagem);
    } else {
      setGrantFailures(falhas);
      setNewEmail(falhas.map((f) => f.email).join(", "));
      if (enviados.length > 0) toast.success(`${enviados.length} acesso(s) ao portal concedido(s).`);
    }
    if (enviados.length > 0) load(clientId);
    setGranting(false);
  };

  const handleRevoke = async (userId: string, email?: string | null) => {
    if (!clientId) return;
    if (!window.confirm(`Remover o acesso de ${email ?? "este usuário"} ao portal?`)) return;
    try {
      await api.delete(`/Client/Clients/${clientId}/users/${userId}`);
      toast.success("Acesso removido.");
      load(clientId);
    } catch (e: any) {
      console.error("Erro ao remover acesso", e);
      toast.error(mensagemDoServidor(e, "Erro ao remover acesso."));
    }
  };

  const handleConfirmReset = async () => {
    if (!clientId || !reset) return;
    setReset({ ...reset, loading: true });
    try {
      const r = await api.post(`/Client/Clients/${clientId}/users/${reset.userId}/reset-password`);
      setReset({ ...reset, loading: false, temporaryPassword: r.data?.temporaryPassword });
    } catch (e: any) {
      console.error("Erro ao redefinir senha", e);
      setReset(null);
      setSectionError(
        e?.response?.status === 404
          ? "Este usuário não pode ter a senha redefinida por aqui."
          : "Não foi possível redefinir a senha. A senha atual continua valendo; tente de novo."
      );
    }
  };

  const copyPassword = async () => {
    if (!reset?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(reset.temporaryPassword);
      toast.success("Senha copiada.");
    } catch {
      toast.error("Não foi possível copiar. Selecione a senha e copie manualmente.");
    }
  };

  const showingPassword = !!reset?.temporaryPassword;

  return (
    <div className="mt-5 rounded-2xl border border-gray-200 p-4 dark:border-gray-600">
      <span className="text-sm font-semibold text-navy-700 dark:text-white">Acesso ao portal</span>
      <span className="mb-3 block text-xs text-gray-700 dark:text-gray-300">
        Usuários deste cliente que podem entrar no portal (cada um vê apenas os dados deste cliente)
      </span>
      <p className="mb-3 text-[11px] text-gray-700 dark:text-gray-300">
        O que cada usuário vê no portal vem do perfil escolhido; usuário novo nasce só com Chamados, a menos que você escolha outro perfil.
      </p>
      {profilesError && (
        <p role="alert" className="mb-3 text-sm text-red-600 dark:text-red-300">
          Não foi possível carregar os perfis de portal. Feche e abra o cliente para tentar de novo.
        </p>
      )}

      {sectionError && (
        <div
          role="alert"
          className="mb-3 flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300"
        >
          <MdErrorOutline className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {sectionError}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-700 dark:text-gray-300">Carregando…</p>
      ) : loadError ? (
        <p className="mb-3 text-sm text-red-600 dark:text-red-300">{loadError}</p>
      ) : users.length === 0 ? (
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          Nenhum usuário com acesso ainda. Quem for liberado abaixo aparece aqui com as áreas que pode ver.
        </p>
      ) : (
        <ul className="mb-3 flex flex-col gap-2">
          {users.map((u) => {
            const saving = savingIds.includes(u.userId);
            return (
              <li
                key={u.userId}
                className="flex flex-col gap-2 rounded-xl border border-gray-100 px-3 py-2 text-sm dark:border-gray-700"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate font-medium text-navy-700 dark:text-white">
                    {u.email ?? u.userId}
                  </span>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => setReset({ userId: u.userId, email: u.email ?? u.userId, loading: false })}
                      className="rounded-lg border border-dv-blue-200 px-2.5 py-1 text-[11px] font-semibold text-dv-blue-600 transition-all hover:bg-dv-blue-50 dark:border-dv-blue-500/30 dark:text-dv-cyan-300 dark:hover:bg-white/5"
                    >
                      Redefinir senha
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRevoke(u.userId, u.email)}
                      className="rounded-lg border border-red-200 px-2.5 py-1 text-[11px] font-semibold text-red-600 transition-all hover:bg-red-50 dark:border-red-500/30 dark:text-red-300"
                    >
                      Remover acesso
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor={`perfil-${u.userId}`} className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Perfil de portal
                  </label>
                  <select
                    id={`perfil-${u.userId}`}
                    value={u.profileId != null ? String(u.profileId) : ""}
                    disabled={saving || portalProfiles.length === 0}
                    onChange={(e) => e.target.value && handleChangeProfile(u, Number(e.target.value))}
                    className={inputCls}
                  >
                    {u.profileId == null && <option value="">Sem perfil</option>}
                    {portalProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    Áreas liberadas:{" "}
                    {grantedAreas(u, portalProfiles.find((p) => p.id === u.profileId)).join(", ") || "nenhuma"}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="view-client-accessEmail" className={labelCls}>E-mail do usuário</label>
          <textarea
            id="view-client-accessEmail"
            rows={2}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className={`${inputCls} resize-y`}
            placeholder="usuario@empresa.com, outro@empresa.com"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="view-client-accessPassword" className={labelCls}>Senha inicial</label>
          <input
            id="view-client-accessPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputCls}
            placeholder="Se usuário novo"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="view-client-accessProfile" className={labelCls}>Perfil do novo usuário</label>
          <select
            id="view-client-accessProfile"
            value={newProfileId}
            onChange={(e) => setNewProfileId(e.target.value)}
            className={inputCls}
          >
            {/* Sem perfil escolhido, o backend usa "Cliente – Chamados": o contato novo vê só Chamados, como antes. */}
            <option value="">Padrão: só Chamados</option>
            {portalProfiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleGrant}
          disabled={granting || emailsValidos.length === 0 || emailsInvalidos.length > 0}
          className="flex items-center justify-center rounded-xl bg-ink-900 px-4 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
        >
          {granting ? "Concedendo…" : "Conceder acesso"}
        </button>
      </div>
      {emailsInvalidos.length > 0 && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-300">E-mail inválido: {emailsInvalidos.join(", ")}</p>
      )}
      {grantFailures.length > 0 && (
        <div
          role="alert"
          className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300"
        >
          <p className="font-semibold">Não foi possível conceder acesso a:</p>
          <ul className="list-disc pl-5">
            {grantFailures.map((f) => (
              <li key={f.email}>
                {f.email}: {f.mensagem}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-700 dark:text-gray-300">
        Separe vários e-mails por vírgula ou cole um por linha; senha e perfil valem para todos. Se o e-mail já tiver login no NINA, ele será vinculado a este cliente sem alterar a senha.
      </p>

      <Modal open={!!reset} size="xs" backdrop="static" onClose={() => setReset(null)}>
        <Modal.Header>
          <Modal.Title>{showingPassword ? "Senha temporária" : "Redefinir senha"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showingPassword ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Nova senha de <strong>{reset?.email}</strong>:
              </p>
              <div className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2 dark:bg-navy-900">
                <code className="select-all text-base font-bold tracking-wider text-navy-700 dark:text-white">
                  {reset?.temporaryPassword}
                </code>
                <button
                  type="button"
                  aria-label="Copiar senha"
                  onClick={copyPassword}
                  className="rounded-lg p-1.5 text-dv-blue-600 hover:bg-dv-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 dark:text-dv-cyan-300 dark:hover:bg-white/5"
                >
                  <MdContentCopy className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                Anote agora — ela não será mostrada de novo. No primeiro acesso a pessoa vai trocar a senha.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              A senha atual deixa de funcionar. Será gerada uma senha temporária para{" "}
              <strong>{reset?.email}</strong>, que deverá ser trocada no primeiro acesso.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {showingPassword ? (
            <button
              type="button"
              onClick={() => setReset(null)}
              className="rounded-xl bg-ink-900 px-4 py-2 text-sm font-bold text-white dark:bg-white dark:text-ink-900"
            >
              Fechar
            </button>
          ) : (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReset(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-gray-600 dark:text-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmReset}
                disabled={reset?.loading}
                className="rounded-xl bg-ink-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50 dark:bg-white dark:text-ink-900"
              >
                {reset?.loading ? "Redefinindo…" : "Confirmar redefinição"}
              </button>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PortalUsersSection;
